import { useCallback, useState } from "react";
import { TableRow } from "@kanda-libs/ks-component-ts/dist";
import { ORDER, SORTING } from "components/loan/LoanTable/constants";
import { useHistory } from "react-router-dom";

export interface LoanTableSortingAction {
  type?: string;
}

export interface LoanTableSortingHook {
  sorting: string;
  order: string;
  onAction: (action?: LoanTableSortingAction) => void;
  onRowClicked: (row?: TableRow) => void;
}

export default function useLoanTableSorting(): LoanTableSortingHook {
  const { push } = useHistory();
  const [sorting, setSorting] = useState(SORTING.UPDATED_AT);
  const [order, setOrder] = useState(ORDER.NORMAL);

  /**
   * Handles row click
   */
  const onRowClicked = useCallback(
    (row?: TableRow) => {
      if (row?.original?.link) {
        push(row.original.link);
      }
    },
    [push]
  );

  const onAction = useCallback(
    (action: LoanTableSortingAction = {}) => {
      const { type } = action;

      if (type === "price-ascending") {
        setSorting(SORTING.PRICE);
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "price-descending") {
        setSorting(SORTING.PRICE);
        setOrder(ORDER.INVERSE);
        return;
      }

      if (type === "id-alphabetical") {
        if (sorting !== SORTING.ID) {
          setSorting(SORTING.ID);
          setOrder(ORDER.NORMAL);
          return;
        }
        if (order === ORDER.NORMAL) {
          setOrder(ORDER.INVERSE);
          return;
        }
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "loan-ascending") {
        setSorting(SORTING.LOAN);
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "loan-descending") {
        setSorting(SORTING.LOAN);
        setOrder(ORDER.INVERSE);
        return;
      }

      if (type === "deposit-ascending") {
        setSorting(SORTING.DEPOSIT);
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "deposit-descending") {
        setSorting(SORTING.DEPOSIT);
        setOrder(ORDER.INVERSE);
        return;
      }

      if (type === "job-alphabetical") {
        if (sorting !== SORTING.JOB_TITLE) {
          setSorting(SORTING.JOB_TITLE);
          setOrder(ORDER.NORMAL);
          return;
        }
        if (order === ORDER.NORMAL) {
          setOrder(ORDER.INVERSE);
          return;
        }
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "job-id-alphabetical") {
        if (sorting !== SORTING.JOB_ID) {
          setSorting(SORTING.JOB_ID);
          setOrder(ORDER.NORMAL);
          return;
        }
        if (order === ORDER.NORMAL) {
          setOrder(ORDER.INVERSE);
          return;
        }
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "name-alphabetical") {
        if (sorting !== SORTING.CUSTOMER) {
          setSorting(SORTING.CUSTOMER);
          setOrder(ORDER.NORMAL);
          return;
        }
        if (order === ORDER.NORMAL) {
          setOrder(ORDER.INVERSE);
          return;
        }
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "company-name-alphabetical") {
        if (sorting !== SORTING.COMPANY) {
          setSorting(SORTING.COMPANY);
          setOrder(ORDER.NORMAL);
          return;
        }
        if (order === ORDER.NORMAL) {
          setOrder(ORDER.INVERSE);
          return;
        }
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "activity-ascending") {
        setSorting(SORTING.UPDATED_AT);
        setOrder(ORDER.NORMAL);
        return;
      }

      if (type === "activity-descending") {
        setSorting(SORTING.UPDATED_AT);
        setOrder(ORDER.INVERSE);
        return;
      }

      setSorting(SORTING.UPDATED_AT);
      setOrder(ORDER.NORMAL);
    },
    [sorting, order]
  );

  return {
    sorting,
    order,
    onAction,
    onRowClicked,
  };
}
